import TrendingEtfSecurityClickedAnalytics from '@/common/analytics/events/TrendingEtfSecurityClickedAnalytics'
import { APP_ROUTES } from '@/common/constants'
import { H2 } from '@/common/ui/h2'
import { PriceDiff } from '@/common/ui/PriceDiff'
import { InfoIconTooltip } from '@/common/ui/tooltip'
import { INVEST_COPY } from '@/locales/en'
import { SecurityAvatar } from '@/securities/securityAvatar'
import { useFetchTrendingSecurities } from '@/trading/tradingQueries'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useOrderSource } from '@/common/context/useOrderSource'
import { ORDER_ENTRY_POINT, OrderFrom } from '@/common/context/orderSourceValues'

type Props = {
    securityCode: string
    securityName: string
    changeInPriceYTD: number
    orderFrom: OrderFrom
}
const SecurityCard: React.FC<Props> = ({ securityCode, securityName, changeInPriceYTD, orderFrom }) => {
    const { setOrderSource } = useOrderSource()
    const handleClick = () => {
        setOrderSource(orderFrom, ORDER_ENTRY_POINT.TRENDING_ETF)
        TrendingEtfSecurityClickedAnalytics.track({
            security_code: securityCode,
            change_in_price_ytd: changeInPriceYTD,
        })
    }

    return (
        <Link
            key={securityCode}
            className="text-current group flex-none"
            to={`${APP_ROUTES.SECURITY}/${securityCode}`}
            onClick={handleClick}
        >
            <div className="w-36 md:w-52 p-6 bg-white rounded-lg border outline outline-1 outline-offset-[-0.50px] outline-neutral-300 inline-flex flex-col justify-start items-start gap-4">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <SecurityAvatar securityCode={securityCode} height={8} width={8} />
                    <div className="self-stretch justify-start text-black font-medium font-['Inter'] leading-5 line-clamp-1">
                        {securityCode}
                    </div>
                    <div className="self-stretch h-10 justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5 line-clamp-2">
                        {securityName}
                    </div>
                </div>
                <div className="flex gap-[2px] text-[#57534E] font-normal min-w-12">
                    {!isNaN(changeInPriceYTD) && (
                        <>
                            <PriceDiff
                                className="font-medium"
                                value={changeInPriceYTD}
                                label={`${Math.abs(changeInPriceYTD)}%`}
                            />
                            (YTD)
                        </>
                    )}
                </div>
            </div>
        </Link>
    )
}

const SecurityCardSkeleton: React.FC = () => {
    return (
        <div className="text-current group flex-none">
            <div className="w-36 md:w-52 p-6 bg-white rounded-lg border animate-pulse inline-flex flex-col justify-start items-start gap-4">
                <div className="h-8 w-8 bg-stone-100 rounded" />
                <div className="h-4 w-32 bg-stone-100 rounded" />
                <div className="h-4 w-full bg-stone-100 rounded" />
                <div className="h-4 w-20 bg-stone-100 rounded mt-auto" />
            </div>
        </div>
    )
}

export function TrendingEtfs(props: { className?: string; orderFrom: OrderFrom }): React.JSX.Element {
    const trendingCarouselRef = useRef<HTMLDivElement>(null)
    const { data: trendingSecurities, isLoading: isLoadingTrending } = useFetchTrendingSecurities()
    const SKELETON_CARDS = [1, 2, 3, 4]
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const scroll = (direction: 'left' | 'right') => {
        if (trendingCarouselRef.current) {
            const { clientWidth } = trendingCarouselRef.current
            let scrollAmount = clientWidth / 2
            if (direction === 'left') scrollAmount *= -1
            trendingCarouselRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
            })
        }
    }

    const checkScrollPosition = () => {
        if (trendingCarouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = trendingCarouselRef.current
            setCanScrollLeft(scrollLeft > 0)
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth)
        }
    }

    useEffect(() => {
        checkScrollPosition()
        const container = trendingCarouselRef.current
        if (container) {
            container.addEventListener('scroll', checkScrollPosition)
            return () => container.removeEventListener('scroll', checkScrollPosition)
        }
    }, [isLoadingTrending])

    return (
        <div
            className={`w-full relative self-stretch inline-flex flex-col justify-start items-start min-w-0 gap-6 ${props.className ?? ''}`}
        >
            <div className="flex items-center gap-4">
                <H2 className="justify-center text-black text-3xl md:text-3xl font-normal font-['Serious Sans'] leading-10 mb-0">
                    {INVEST_COPY.trendingEtfsTitle}
                </H2>
                <div>
                    <InfoIconTooltip>{INVEST_COPY.trendingEtfsTooltip}</InfoIconTooltip>
                </div>
            </div>
            <div className="gap-6 flex items-stretch overflow-x-auto w-full" ref={trendingCarouselRef}>
                {isLoadingTrending
                    ? SKELETON_CARDS.map((i) => <SecurityCardSkeleton key={i} />)
                    : trendingSecurities?.map((security: any) => {
                          return (
                              <SecurityCard
                                  key={security.securityDetails.securityCode}
                                  securityCode={security.securityDetails.securityCode}
                                  securityName={
                                      security.securityDetails.companyName ??
                                      security.securityName ??
                                      security.securityDetails.securityName
                                  }
                                  changeInPriceYTD={security.changeInPriceYTD}
                                  orderFrom={props.orderFrom}
                              />
                          )
                      })}
            </div>
            {canScrollLeft ? (
                <button
                    onClick={() => scroll('left')}
                    className="absolute left-2 rounded-full border-2 p-1 top-1/2 -transform-y-1/2 bg-background"
                >
                    <ArrowLeft />
                </button>
            ) : null}
            {canScrollRight ? (
                <button
                    onClick={() => scroll('right')}
                    className="absolute right-2 rounded-full border-2 p-1 top-1/2 -transform-y-1/2 bg-background"
                >
                    <ArrowRight />
                </button>
            ) : null}
        </div>
    )
}
